import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { loadInstallmentsByCompany, getInstallmentStatus } from '../../../core/utils/installment';
import { groupReceivableRows } from '../../../core/utils/receivableGrouping';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import {
  ENTRY_TYPE,
  INSTALLMENT_STATUS,
  INSTALLMENT_STATUS_FILTERS,
} from '../../../core/constants/financial';
import { getRangeFromFilter, getPeriodLabel, todayISO } from '../../../core/utils/financialPeriod';
import { useLogoPreloader } from '../../../core/hooks/useLogoPreloader';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import FinancialListModal from './FinancialListModal';
import FinancialInstallmentsPrint from './FinancialInstallmentsPrint';
import PeriodFilter from './PeriodFilter';
import StatusFilter from './StatusFilter';
import PaymentModal from './PaymentModal';
import FinancialEntryModal from './FinancialEntryModal';

const PRINT_PAGE_STYLE = `
  @page { size: A4 portrait; margin: 10mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const getStatusFilterLabel = (key) => {
  const option = INSTALLMENT_STATUS_FILTERS.find((s) => s.key === key);
  return option ? option.label : '';
};

// Referência estável — inline recriaria o objeto a cada render e o efeito de
// prefill do FinancialEntryModal apagaria o form digitado.
const NEW_ENTRY_PREFILL = { type: ENTRY_TYPE.RECEITA };

const SORT_ACCESSORS = {
  cliente: (i) => i.financial_entry?.supplier_client || '',
  plano: (i) => i.financial_entry?.AccountPlan?.name || '',
  vencimento: (i) => i.due_date || '',
  valor: (i) => Number(i.value) || 0,
  parcela: (i) => i.installment_number || '',
  situacao: (i) => getInstallmentStatus(i),
};

// Situação exibida por parcela: recebido > atrasado (pendente vencido) > pendente.
const getStatusDisplay = (inst) => {
  if (inst.status === INSTALLMENT_STATUS.PAGO) {
    return { text: 'RECEBIDO', color: '#70E000', bg: 'rgba(112,224,0,0.15)' };
  }
  if (inst.due_date && inst.due_date < todayISO()) {
    return { text: 'ATRASADO', color: '#ff5a5a', bg: 'rgba(255,90,90,0.15)' };
  }
  return { text: 'PENDENTE', color: '#e88c2a', bg: 'rgba(232,140,42,0.15)' };
};

const AccountsReceivableModal = ({ isOpen, onClose, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});
  const [installments, setInstallments] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todos');
  const [customRange, setCustomRange] = useState({ from: todayISO(), to: todayISO() });
  const [statusFilter, setStatusFilter] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setFilter('todos');
    setCustomRange({ from: todayISO(), to: todayISO() });
    setStatusFilter([]);
  }, [isOpen]);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [editEntryId, setEditEntryId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const printRef = useRef();
  const { logoBase64, waitForReady } = useLogoPreloader(company?.logo);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: PRINT_PAGE_STYLE,
    onBeforeGetContent: () => waitForReady(4000),
  });

  const range = getRangeFromFilter(filter, customRange);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    loadInstallmentsByCompany(company.id, {
      type: ENTRY_TYPE.RECEITA,
      dueDateFrom: range.from,
      dueDateTo: range.to,
    }).then((data) => {
      if (cancelled) return;
      setInstallments(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, filter, customRange.from, customRange.to, refreshKey]);

  // Cobranças de OS aparecem unificadas (1 linha por parcela, soma das
  // categorias); o rateio por categoria segue intacto no banco para a DRE.
  const rows = groupReceivableRows(installments);

  const matchesSearch = (row, term) => {
    const client = row.financial_entry?.supplier_client?.toLowerCase() || '';
    const account = row.financial_entry?.AccountPlan?.name?.toLowerCase() || '';
    if (client.includes(term) || account.includes(term)) return true;
    if (!row.children) return false;
    return row.children.some((child) =>
      (child.financial_entry?.AccountPlan?.name?.toLowerCase() || '').includes(term)
    );
  };

  const filtered = rows.filter((i) => {
    if (statusFilter.length > 0 && !statusFilter.includes(getInstallmentStatus(i))) return false;
    const term = search.toLowerCase();
    if (!term) return true;
    return matchesSearch(i, term);
  });

  // Grupos parcialmente recebidos contam só as partes ainda pendentes.
  const pendingValueOf = (row) => {
    if (row.isGroup) return Number(row.pendingValue) || 0;
    if (row.status === INSTALLMENT_STATUS.PAGO) return 0;
    return Number(row.value) || 0;
  };

  const totalPendente = filtered.reduce((sum, i) => sum + pendingValueOf(i), 0);

  const { sorted, sort, toggleSort } = useTableSort(filtered, SORT_ACCESSORS);

  const handleRowClick = (row) => {
    // Linha de rateio de OS abre o lançamento subjacente (1ª categoria); por ser
    // rateio, o FinancialEntryModal mostra o banner com o total da OS e o nº de contas.
    const entryId =
      row.financial_entry_id ||
      row.financial_entry?.id ||
      row.children?.[0]?.financial_entry_id;
    if (entryId) setEditEntryId(entryId);
  };

  // No grupo, o PaymentModal recebe (e exibe) só o que ainda falta receber.
  const openReceive = (row) => {
    if (row.isGroup) {
      setPaymentTarget({ ...row, value: row.pendingValue });
      return;
    }
    setPaymentTarget(row);
  };

  return (
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title="Contas a Receber"
      badge={`${formatCurrency(totalPendente)} pendente`}
      searchPlaceholder="Buscar cliente ou conta..."
      search={search}
      onSearchChange={setSearch}
      onNew={() => setCreateOpen(true)}
      newLabel="+ Adicionar Lançamento"
      headerExtra={
        <button type="button" className="fd-btn-green" onClick={handlePrint}>
          Imprimir
        </button>
      }
    >
      <PeriodFilter
        filter={filter}
        onFilterChange={setFilter}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      <StatusFilter value={statusFilter} onChange={setStatusFilter} />

      <div className="fd-modal-table-wrapper">
        <table className="fd-modal-table">
          <thead>
            <tr>
              <SortableTh label="Cliente" sortKey="cliente" sort={sort} onSort={toggleSort} />
              <SortableTh label="Plano de Contas" sortKey="plano" sort={sort} onSort={toggleSort} />
              <SortableTh label="Vencimento" sortKey="vencimento" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
              <SortableTh label="Valor" sortKey="valor" sort={sort} onSort={toggleSort} style={{ width: 130, textAlign: 'right' }} />
              <SortableTh label="Parcela" sortKey="parcela" sort={sort} onSort={toggleSort} style={{ width: 80, textAlign: 'center' }} />
              <SortableTh label="Situação" sortKey="situacao" sort={sort} onSort={toggleSort} style={{ width: 100, textAlign: 'center' }} />
              <th style={{ width: 100, textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((inst) => {
              const isPago = inst.status === INSTALLMENT_STATUS.PAGO;
              const sit = getStatusDisplay(inst);
              const rowTitle = inst.isGroup
                ? 'Clique para abrir o lançamento (rateio da OS)'
                : 'Clique para abrir o lançamento';
              return (
              <tr
                key={inst.id}
                onClick={() => handleRowClick(inst)}
                style={{ cursor: 'pointer' }}
                title={rowTitle}
              >
                <td>{inst.financial_entry?.supplier_client || '—'}</td>
                <td className="fd-table-muted">
                  {inst.financial_entry?.AccountPlan?.name || '—'}
                </td>
                <td style={{ color: '#70E000' }}>{formatDateBR(inst.due_date)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(inst.value)}</td>
                <td className="fd-table-muted" style={{ textAlign: 'center' }}>{inst.installment_number}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className="fd-status-badge" style={{ background: sit.bg, color: sit.color }}>
                    {sit.text}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {isPago ? (
                    <span className="fd-table-muted">—</span>
                  ) : (
                    <button
                      type="button"
                      className="fd-btn-green"
                      style={{ padding: '5px 12px', fontSize: 11 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openReceive(inst);
                      }}
                    >
                      RECEBER
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="fd-table-empty">Nenhuma conta a receber no período</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1000px' }}>
        <FinancialInstallmentsPrint
          ref={printRef}
          company={company}
          logoBase64={logoBase64}
          reportTitle="Contas a Receber"
          entityLabel="Cliente"
          periodLabel={getPeriodLabel(filter, customRange)}
          statusLabel={statusFilter.map(getStatusFilterLabel).join(', ')}
          searchTerm={search}
          data={sorted}
        />
      </div>

      <PaymentModal
        isOpen={!!paymentTarget}
        installment={paymentTarget}
        mode="receber"
        onClose={() => setPaymentTarget(null)}
        onConfirmed={() => {
          setRefreshKey((k) => k + 1);
          if (onChanged) onChanged();
        }}
      />

      <FinancialEntryModal
        isOpen={createOpen || editEntryId != null}
        entryId={editEntryId}
        prefilled={NEW_ENTRY_PREFILL}
        onClose={() => {
          setCreateOpen(false);
          setEditEntryId(null);
        }}
        onSaved={() => {
          setRefreshKey((k) => k + 1);
          if (onChanged) onChanged();
        }}
      />
    </FinancialListModal>
  );
};

export default AccountsReceivableModal;
